import './styles/AgentAuthEmployeeNumberStyle.css';
import React, { useState } from 'react';
import {
  ControlledNumericInput,
  NavigationButton,
  NumPad,
  ToastType,
  TopNav,
  TopNavToastMessage,
} from '../common';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import { useHistory } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Credential from '../../resources/images/credential.png';
import { apiErrorHandler, apiPOST } from '../../restApi';
import {
  EmployeeLoginRequestPOST,
  EmployeeLoginResponsePOST,
  SessionRequestPOST,
  SessionResponsePOST,
} from '../../models/cruds';

const VALID_LENGTH = 10;
const VALIDATE_EMPLOYEE_NUMBER_ERROR_FEEDBACK_MESSAGE = 'Error en la consulta.';
const VALIDATE_EMPLOYEE_NUMBER_LENGTH_FEEDBACK_MESSAGE =
  'El número de empleado es de 10 caractéres.';
const VALIDATE_EMPLOYEE_NUMBER_FEEDBACK_MESSAGE =
  'Por favor ingresa un número de empleado.';
const VALIDATE_EMPLOYEE_NUMBER_UNAUTHORIZED_FEEDBACK_MESSAGE =
  'No está autorizado';
const VALIDATE_EMPLOYEE_NUMBER_ERROR_RESPONSE_FEEDBACK_MESSAGE =
  'Error en la respuesta';

const AgentAuthEmployeeNumber: React.FC = () => {
  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [variableInError, setVariableInError] = useState('');
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const token = useTypedSelector(({ session }) => {
    return session.dhlApiToken;
  });
  const lockerInstallation = useTypedSelector(({ kioskData }) => {
    return kioskData.installation;
  });
  const {
    sessionUpdateDHLTokenExpired,
    updateEmployeeNumber,
    sessionUpdateAgentToken,
  } = useActions();
  const openEmployeeSession = async (agentToken: string): Promise<boolean> => {
    let request = new SessionRequestPOST();
    let response: SessionResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(lockerInstallation.id.toString()),
      request.generateConfig(agentToken)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        if (response.data.session) {
          return true;
        }
      } else {
        errorMsg = apiErrorHandler(response);
        if (errorMsg === APIResponseStatusType.UNAUTHORIZED) {
          sessionUpdateDHLTokenExpired(true);
          return false;
        }
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    return false;
  };
  const requestEmployeeValidation = async (): Promise<boolean> => {
    if (employeeNumber === '') {
      setFeedbackMessage(VALIDATE_EMPLOYEE_NUMBER_FEEDBACK_MESSAGE);
      return false;
    }
    setLoading(true);
    let request = new EmployeeLoginRequestPOST();
    let response: EmployeeLoginResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(employeeNumber),
      request.generateConfig(token)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        if (
          response.data.token &&
          (await openEmployeeSession(response.data.token))
        ) {
          sessionUpdateAgentToken(response.data.token);
          history.replace(`/${navigationPages.AGENT_PROCESS_SELECTION}`);
        } else {
          //TODO check for unauth and refresh token
          errorMsg = 'Unauthorized';
        }
      } else {
        errorMsg = apiErrorHandler(response);
        if (
          errorMsg === VALIDATE_EMPLOYEE_NUMBER_UNAUTHORIZED_FEEDBACK_MESSAGE
        ) {
          updateEmployeeNumber(employeeNumber);
          history.replace(`/${navigationPages.AGENT_AUTH_YELLOW_PASSWORD}`);
        } else {
          setFeedbackMessage(
            VALIDATE_EMPLOYEE_NUMBER_ERROR_RESPONSE_FEEDBACK_MESSAGE
          );
        }
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setLoading(false);
    setFeedbackMessage(VALIDATE_EMPLOYEE_NUMBER_ERROR_FEEDBACK_MESSAGE);
    return false;
  };
  const handleKeypress = (e: any) => {
    //it triggers by pressing the enter key
    if (e.key === 'Enter') {
      requestEmployeeValidation();
    }
  };
  const handleNumPad = (option: string) => {
    setFeedbackMessage('');
    //it triggers by pressing the enter key
    if (option === 'go') {
      requestEmployeeValidation();
      return;
    }
    if (option === 'back') {
      setEmployeeNumber(employeeNumber.slice(0, -1));
      return;
    }
    if (employeeNumber.length < VALID_LENGTH) {
      setEmployeeNumber(employeeNumber + option);
    } else {
      setFeedbackMessage(VALIDATE_EMPLOYEE_NUMBER_LENGTH_FEEDBACK_MESSAGE);
    }
  };
  const renderNumpad = () => {
    if (loading) {
      return (
        <div className="employee-number-numpad-container">
          <div className="spinner-container">
            <Spinner className="spinner" animation="grow" variant="danger" />
          </div>
        </div>
      );
    }
    return (
      <div className="employee-number-numpad-container">
        <NumPad
          onClickButton={(option) => {
            handleNumPad(option);
          }}
        />
      </div>
    );
  };
  const renderContent = () => {
    return (
      <div className="locker-content-container">
        <div className="locker-content-container-left">
          <div className="employee-number-scan-awb-container">
            <ControlledNumericInput
              val={employeeNumber}
              inputLabel="Número de Empleado"
              inputPlaceholder="12345467895"
              validationError={variableInError === 'employeeNumber'}
              maxLength={10}
              maxNumber={9999999999}
              disabled={loading}
              type="integer"
              inputType="password"
              onError={(isErr: boolean) => {
                if (isErr) {
                  let msg = languageMap.login.installation_id_error[language];
                  setVariableInError('employeeNumber');
                  setFeedbackMessage(msg);
                } else {
                  setFeedbackMessage('');
                  setVariableInError('');
                }
              }}
              onTextChange={(newVal: string) => {
                setVariableInError('');
                setFeedbackMessage('');
                setEmployeeNumber(newVal);
              }}
              handleKeypress={handleKeypress}
              extraClassContainer="no-padding-right full-width stretched"
              extraClass="placeholder-left"
            />

            <div className="employee-number-awb-image-awb-container">
              <img
                id="awb-image"
                className="awb-image"
                src={Credential}
                alt="Credential"
              />
            </div>
          </div>
        </div>
        <div className="locker-content-container-right">{renderNumpad()}</div>
      </div>
    );
  };
  return (
    <div id="clientRetrievalIssues" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container fullpage">
          <TopNavToastMessage
            toastText={feedbackMessage}
            toastType={ToastType.ERROR}
            resetMessage={() => setFeedbackMessage('')}
          />
          <div className="title-content-container">
            <p className="header nopadding nomargin">
              {languageMap.agent_auth_employee_number.title[language]}
            </p>
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={
                  languageMap.client_retrieval_scan_awb.back_button[language]
                }
                buttonClass="navigation-button cancel-button complete"
                onClickButton={() => {
                  history.replace(
                    `/${navigationPages.CLIENT_RETRIEVAL_SCAN_AWB}`
                  );
                }}
              />
            </div>
            <div className="footer-button-container" />
          </div>
        </div>
      </div>
    </div>
  );
};

export { AgentAuthEmployeeNumber };
